// src/components/templates/organisation/BookingWidget.jsx
import React, { useState } from 'react';
import { Calendar, Clock, ArrowRight, X } from 'lucide-react';
import { BookingFormBuilder } from '../../manager/booking/BookingFormBuilder';

export const BookingWidget = ({ 
  bookData, 
  bookId, 
  isFloating = false, 
  onInteraction 
}) => {
  const [showBookingForm, setShowBookingForm] = useState(false);
  
  if (!bookData?.enableBooking) return null;

  const services = bookData?.bookingServices || {};
  const enabledServices = Object.entries(services)
    .filter(([_, enabled]) => enabled)
    .map(([service, _]) => service);

  if (enabledServices.length === 0) return null;

  const serviceLabels = {
    appointment: { emoji: '📋', label: 'Book Appointment' },
    consultation: { emoji: '💡', label: 'Schedule Consultation' },
    class: { emoji: '🎓', label: 'Join Class' },
    facility: { emoji: '🏢', label: 'Book Facility' },
    event: { emoji: '📅', label: 'Register for Event' }
  };

  if (isFloating) {
    return (
      <>
        {/* Floating Action Button */}
        <button
          onClick={() => {
            setShowBookingForm(true);
            onInteraction?.('booking_widget_opened');
          }}
          className="fixed bottom-20 right-6 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-all hover:scale-110 z-40"
        >
          <Calendar className="h-6 w-6" />
        </button>

        {/* Booking Modal */}
        {showBookingForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Book with Us</h2>
                <button
                  onClick={() => setShowBookingForm(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="p-4 overflow-y-auto max-h-[calc(90vh-100px)]">
                <BookingInterface 
                  bookData={bookData}
                  bookId={bookId}
                  onClose={() => setShowBookingForm(false)}
                  onInteraction={onInteraction}
                />
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Inline widget
  return (
    <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl p-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold mb-2">Ready to get started?</h3>
          <p className="opacity-90">Book your appointment or consultation today</p>
        </div>
        
        <button
          onClick={() => {
            setShowBookingForm(true);
            onInteraction?.('booking_widget_clicked');
          }}
          className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2"
        >
          Book Now
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>

      {/* Service Quick Options */}
      <div className="mt-4 flex flex-wrap gap-2">
        {enabledServices.map(service => (
          <span
            key={service}
            className="bg-white/20 px-3 py-1 rounded-full text-sm"
          >
            {serviceLabels[service]?.emoji} {serviceLabels[service]?.label}
          </span>
        ))}
      </div>

      {/* Booking Modal */}
      {showBookingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Book with {bookData?.bookingOrgName || bookData?.botName}</h2>
              <button
                onClick={() => setShowBookingForm(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="overflow-y-auto max-h-[calc(90vh-100px)]">
              <BookingInterface 
                bookData={bookData}
                bookId={bookId}
                onClose={() => setShowBookingForm(false)}
                onInteraction={onInteraction}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Public Booking Interface
const BookingInterface = ({ bookData, bookId, onClose, onInteraction }) => {
  const [selectedService, setSelectedService] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);

  const services = bookData?.bookingServices || {};
  const enabledServices = Object.entries(services)
    .filter(([_, enabled]) => enabled)
    .map(([service, _]) => service);

  const serviceConfigs = {
    appointment: {
      title: 'Book an Appointment',
      description: 'Schedule a one-on-one meeting with our team',
      icon: '📋',
      color: 'bg-blue-100 text-blue-700'
    },
    consultation: {
      title: 'Schedule Consultation',
      description: 'Get expert advice and strategic guidance',
      icon: '💡',
      color: 'bg-purple-100 text-purple-700'
    },
    class: {
      title: 'Join a Class',
      description: 'Participate in workshops and training sessions',
      icon: '🎓',
      color: 'bg-green-100 text-green-700'
    },
    facility: {
      title: 'Book Facilities',
      description: 'Reserve meeting rooms and event spaces',
      icon: '🏢',
      color: 'bg-orange-100 text-orange-700'
    }
  };

  if (!selectedService) {
    return (
      <div className="p-6">
        <div className="text-center mb-8">
          <h3 className="text-2xl font-semibold text-gray-900 mb-2">What would you like to book?</h3>
          <p className="text-gray-600">Choose from our available services</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {enabledServices.map(service => {
            const config = serviceConfigs[service];
            return (
              <button
                key={service}
                onClick={() => {
                  setSelectedService(service);
                  onInteraction?.(`booking_service_selected_${service}`);
                }}
                className="text-left p-6 border border-gray-200 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className={`w-12 h-12 rounded-lg ${config.color} flex items-center justify-center text-2xl mb-4`}>
                  {config.icon}
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">{config.title}</h4>
                <p className="text-gray-600 text-sm">{config.description}</p>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Show booking form for selected service
  return (
    <div className="p-6">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => setSelectedService(null)}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          ←
        </button>
        <div>
          <h3 className="text-xl font-semibold text-gray-900">
            {serviceConfigs[selectedService]?.title}
          </h3>
          <p className="text-gray-600">{serviceConfigs[selectedService]?.description}</p>
        </div>
      </div>

      {/* This would integrate with your BookingFormBuilder component */}
      <BookingFormBuilder
        service={{ type: selectedService }}
        isPreview={true}
        bookData={bookData}
        bookId={bookId}
        onComplete={() => {
          onInteraction?.('booking_completed');
          onClose();
        }}
      />
    </div>
  );
};